'use client';

import { useState } from 'react';
import { useCollections } from '@/hooks/useCollections';
import Image from 'next/image';
import Link from 'next/link';
import { Sparkles, ArrowRight, ImageIcon } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/translations';
import { CollectionCardSkeleton } from './components/CollectionCardSkeleton';

export default function CollectionsPageClient() {
  const { language } = useLanguage();
  const t = useTranslation(language);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);

  const { data, isLoading, error } = useCollections({
    page: 1,
    limit: 50,
    search: searchQuery,
    featured: showFeaturedOnly || undefined,
  });

  const collections = data?.data || [];

  const clearFilters = () => {
    setSearchQuery('');
    setShowFeaturedOnly(false);
  };

  return (
    <main className="organic min-h-screen">
      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ padding: 'calc(64px + var(--space-8) * 1.6) clamp(20px, 4vw, 48px) var(--space-8)' }}
      >
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ right: -100, top: -120, width: 280, height: 280, background: 'var(--color-accent-200)', zIndex: 0 }}
        />
        <div className="max-w-7xl mx-auto relative" style={{ zIndex: 1, maxWidth: 640 }}>
          <span
            className="block uppercase mb-2"
            style={{ fontSize: 13, letterSpacing: '0.06em', fontWeight: 600, color: 'var(--color-accent-700)' }}
          >
            Curate cu grijă
          </span>
          <h1 className="mb-3" style={{ fontSize: 'clamp(34px, 4.4vw, 54px)', lineHeight: 1.08, margin: '0 0 var(--space-3)' }}>
            {t('collections.title')}
          </h1>
          <p
            style={{
              fontSize: 16.5, lineHeight: 1.6,
              color: 'color-mix(in srgb, var(--color-text) 78%, transparent)',
              maxWidth: '52ch', margin: 0,
            }}
          >
            {t('collections.subtitle')}
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto" style={{ padding: '0 clamp(20px, 4vw, 48px) calc(var(--space-8) * 2)' }}>
        {/* Search + featured toggle */}
        <div
          className="flex items-center justify-between flex-wrap gap-3"
          style={{ marginBottom: 'var(--space-8)' }}
        >
          <input
            type="text"
            className="input"
            style={{ width: 'min(360px, 100%)' }}
            placeholder={t('collections.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button
            type="button"
            className={`btn ${showFeaturedOnly ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setShowFeaturedOnly((v) => !v)}
          >
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.75} />
            {t('collections.featuredOnly')}
          </button>
        </div>

        {isLoading ? (
          <div
            className="grid"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 'var(--space-6)' }}
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <CollectionCardSkeleton key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="card elev-sm text-center" style={{ padding: 'var(--space-6)' }}>
            <p style={{ color: 'var(--color-accent-700)' }}>{t('collections.errorLoading')}</p>
          </div>
        ) : collections.length > 0 ? (
          <div
            className="grid"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 'var(--space-6)' }}
          >
            {collections.map((collection) => (
              <Link
                key={collection.collectionId}
                href={`/collections/${collection.collectionId}/?collectionId=${collection.collectionId}`}
                className="card elev-sm"
                style={{ padding: 0, overflow: 'hidden' }}
              >
                <div className="relative" style={{ aspectRatio: '4 / 3' }}>
                  {collection.image ? (
                    <Image
                      src={collection.image}
                      alt={collection.name}
                      fill
                      className="washed object-cover"
                      style={{ borderRadius: 0 }}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full" style={{ background: 'var(--color-neutral-100)' }}>
                      <ImageIcon className="h-16 w-16" style={{ color: 'var(--color-neutral-500)' }} />
                    </div>
                  )}

                  {collection.featured && (
                    <span
                      className="tag tag-accent absolute flex items-center gap-1"
                      style={{ top: 12, right: 12 }}
                    >
                      <Sparkles className="h-2.5 w-2.5" strokeWidth={2.75} />
                      {t('collections.featured')}
                    </span>
                  )}

                  <span
                    className="tag tag-outline absolute"
                    style={{ bottom: 12, left: 12, background: 'var(--color-bg)' }}
                  >
                    {collection.products?.length || 0} {t('collections.products')}
                  </span>
                </div>

                <div style={{ padding: 'var(--space-4)' }}>
                  <p className="card-title" style={{ margin: '0 0 var(--space-1)' }}>{collection.name}</p>
                  <p
                    className="line-clamp-2"
                    style={{
                      fontSize: 14, color: 'color-mix(in srgb, var(--color-text) 68%, transparent)',
                      margin: '0 0 var(--space-3)', lineHeight: 1.5,
                    }}
                  >
                    {collection.description}
                  </p>
                  <span
                    className="inline-flex items-center"
                    style={{ gap: 6, fontWeight: 600, fontSize: 14, color: 'var(--color-accent)' }}
                  >
                    {t('collections.viewCollection')}
                    <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.75} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center" style={{ padding: 'calc(var(--space-8) * 2) 0' }}>
            <h3 style={{ margin: '0 0 var(--space-2)' }}>{t('collections.noCollectionsFound')}</h3>
            <p style={{ color: 'color-mix(in srgb, var(--color-text) 65%, transparent)', margin: '0 0 var(--space-3)' }}>
              {searchQuery ? t('collections.tryAdjusting') : t('collections.noCollectionsText')}
            </p>
            {searchQuery && (
              <button onClick={clearFilters} className="btn btn-secondary">
                {t('collections.clearFilters')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Closing CTA band */}
      <section style={{ background: 'var(--color-neutral-100)', padding: 'calc(var(--space-8) * 1.4) clamp(20px, 4vw, 48px)' }}>
        <div className="mx-auto text-center" style={{ maxWidth: 720 }}>
          <h2 style={{ fontSize: 28, margin: '0 0 var(--space-2)' }}>{t('collections.ctaTitle')}</h2>
          <p style={{ color: 'color-mix(in srgb, var(--color-text) 70%, transparent)', margin: '0 0 var(--space-4)' }}>
            {t('collections.ctaSubtitle')}
          </p>
          <div className="flex justify-center flex-wrap" style={{ gap: 'var(--space-3)' }}>
            <Link href="/shop" className="btn btn-primary">{t('collections.browseAll')}</Link>
            <Link href="/contact" className="btn btn-secondary">{t('collections.contactUs')}</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
